import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { MonitorSmartphone, LogOut, ShieldCheck, KeyRound } from "lucide-react";

const field = "w-full bg-transparent border-b border-white/15 py-3 text-base focus:border-gold outline-none transition-colors placeholder:text-text-muted/50";
const btn = "px-6 py-3 rounded-full border border-gold/50 text-gold text-xs uppercase tracking-[0.15em] hover:bg-gold/10 transition-colors disabled:opacity-40";
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export function ActiveSessions() {
  const [data, setData] = useState(null);
  const load = () => api.get("/account/sessions").then(({ data }) => setData(data)).catch(() => setData({ sessions: [] }));
  useEffect(() => { load(); }, []);
  const revoke = async (s) => {
    try { await api.delete(`/account/sessions/${s.sid}`); toast.success(`${s.device} signed out`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const revokeOthers = async () => {
    try { const { data } = await api.post("/account/sessions/revoke-others"); toast.success(`${data.revoked} other device${data.revoked === 1 ? "" : "s"} signed out`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const others = (data?.sessions || []).filter((s) => !s.current).length;
  return (
    <div data-testid="active-sessions" className="rounded-2xl border border-white/10 bg-surface p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><MonitorSmartphone size={14} /> Signed-in devices</p>
        {others > 0 && <button data-testid="sessions-revoke-others" onClick={revokeOthers} className="text-[10px] uppercase tracking-[0.15em] text-text-muted hover:text-amber-300">Sign out all others</button>}
      </div>
      {!data ? <p className="text-sm text-text-muted">Loading…</p> : (
        <ul className="divide-y divide-white/5">
          {data.sessions.map((s) => (
            <li key={s.sid} data-testid={`session-${s.sid}`} className="py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm truncate">{s.device} {s.current && <span data-testid="session-current" className="ml-2 text-[9px] uppercase tracking-[0.12em] text-gold border border-gold/40 px-1.5 rounded">this device</span>}</p>
                <p className="text-[11px] text-text-muted truncate">{s.ip} · signed in {fmt(s.created_at)}{s.remember ? " · 30-day session" : ""}</p>
              </div>
              {!s.current && (
                <button data-testid={`session-revoke-${s.sid}`} onClick={() => revoke(s)} title="Sign out this device"
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-text-muted text-[10px] uppercase tracking-[0.12em] hover:text-amber-300 hover:border-amber-500/40 transition-colors"><LogOut size={11} /> Sign out</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TwoFactorToggle() {
  const { user, refresh } = useAuth();
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const on = !!user?.two_factor_email;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/account/2fa", { enabled: !on, password: user.has_password ? pw : undefined });
      toast.success(!on ? "Two-factor sign-in is on" : "Two-factor sign-in is off"); setPw(""); await refresh();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!user?.has_password) return null;
  return (
    <form data-testid="two-factor-form" onSubmit={submit} className="rounded-2xl border border-white/10 bg-surface p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold"><ShieldCheck size={14} /> Two-factor sign-in</p>
        <span data-testid="two-factor-status" className={`text-[10px] uppercase tracking-[0.12em] border px-2.5 py-1 rounded-full ${on ? "text-gold border-gold/40" : "text-text-muted border-white/15"}`}>{on ? "On" : "Off"}</span>
      </div>
      <p className="text-xs text-text-muted">{on ? "Each password sign-in asks for a 6-digit code we email you." : "Add a second step: after your password we email you a 6-digit code."}</p>
      <div className="flex items-center gap-3"><KeyRound size={14} className="text-text-muted shrink-0" /><input data-testid="two-factor-password" type="password" className={field} placeholder="Confirm with your password" value={pw} onChange={(e) => setPw(e.target.value)} required autoComplete="current-password" /></div>
      <button data-testid="two-factor-submit" disabled={busy} className={btn}>{busy ? "Saving…" : on ? "Turn off" : "Turn on"}</button>
    </form>
  );
}
